import { API_BASE_URL } from './api';
import { authService } from './authService';
import type {
  DiveTrip,
  DiveTripDetail,
  CreateDiveTripRequest,
  UpdateDiveTripRequest,
} from '../types/DiveTrip';

export class DiveTripsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'DiveTripsApiError';
    this.status = status;
  }
}

const throwApiError = async (response: Response, fallback: string): Promise<never> => {
  const payload = await response.json().catch(() => null);
  throw new DiveTripsApiError(payload?.error || fallback, response.status);
};

class DiveTripsService {
  async getMyDiveTrips(): Promise<DiveTrip[]> {
    const response = await fetch(`${API_BASE_URL}/dive-trips`, {
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to fetch dive trips');
    return response.json();
  }

  async getDiveTrip(tripId: string): Promise<DiveTripDetail> {
    const response = await fetch(`${API_BASE_URL}/dive-trips/${encodeURIComponent(tripId)}`, {
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to fetch dive trip');
    return response.json();
  }

  async createDiveTrip(request: CreateDiveTripRequest): Promise<DiveTrip> {
    const response = await fetch(`${API_BASE_URL}/dive-trips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) await throwApiError(response, 'Failed to create dive trip');
    return response.json();
  }

  async updateDiveTrip(tripId: string, request: UpdateDiveTripRequest): Promise<DiveTrip> {
    const response = await fetch(`${API_BASE_URL}/dive-trips/${encodeURIComponent(tripId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) await throwApiError(response, 'Failed to update dive trip');
    return response.json();
  }

  async deleteDiveTrip(tripId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/dive-trips/${encodeURIComponent(tripId)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to delete dive trip');
  }
}

export const diveTripsService = new DiveTripsService();
